import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import type { CustomerDaySummary } from '../domain/customerTypes';
import { getCustomerAccountById, pushCustomerProfileViaServer } from './customerAccounts';
import { invalidateWebCustomerSnapshotAfterWrite } from './customerRepositoryShared';
import { upsertWebCustomerInCache } from './webCustomerSnapshotCache';

async function fetchCustomerDaySummaryRemote(
  branchId: string,
  todayIso: string,
  todayEndIso: string,
): Promise<CustomerDaySummary> {
  const { data, error: remoteError } = await supabase.rpc('reports_branch_fiado_day_summary', {
    p_branch_id: branchId,
    p_from: todayIso,
    p_to: todayEndIso,
  });
  if (remoteError) {
    console.warn('[CustomerDaySummary] RPC reports_branch_fiado_day_summary:', remoteError.message, remoteError.code);
    return { paymentsCount: 0, paymentsTotal: 0, chargesCount: 0, chargesTotal: 0 };
  }
  const row = (data ?? null) as
    | {
        payments_count?: number;
        payments_total?: number;
        charges_count?: number;
        charges_total?: number;
      }
    | null;
  return {
    paymentsCount: Number(row?.payments_count ?? 0),
    paymentsTotal: Number(row?.payments_total ?? 0),
    chargesCount: Number(row?.charges_count ?? 0),
    chargesTotal: Number(row?.charges_total ?? 0),
  };
}

export const getCustomerDaySummary = async (branchId: string | null): Promise<CustomerDaySummary> => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayIso = todayStart.toISOString();
  const todayEnd = new Date(todayStart);
  todayEnd.setHours(23, 59, 59, 999);
  const todayEndIso = todayEnd.toISOString();

  if (!branchId) {
    return {
      paymentsCount: 0,
      paymentsTotal: 0,
      chargesCount: 0,
      chargesTotal: 0,
    };
  }

  const { session } = useAuthStore.getState();
        if (!session) {
          return { paymentsCount: 0, paymentsTotal: 0, chargesCount: 0, chargesTotal: 0 };
        }
        return fetchCustomerDaySummaryRemote(branchId, todayIso, todayEndIso);
};

/** Consulta padrón AFIP/ARCA por CUIT (Edge arca-padron). Devuelve razón social. */
export const updateCustomerNameAndCuit = async (
  customerId: string,
  updates: { name?: string; cuit?: string | null }
): Promise<void> => {
  const now = new Date().toISOString();

  const existing = await getCustomerAccountById(customerId);
        if (!existing) throw new Error('Cliente no encontrado');
        const name = updates.name ?? existing.name;
        const cuit = updates.cuit !== undefined ? updates.cuit : existing.cuit;
        await pushCustomerProfileViaServer('update', {
          scope: 'profile',
          customerId,
          updated_at: now,
          name,
          cuit,
        });
        upsertWebCustomerInCache({
          id: customerId,
          name,
          phone: existing.phone || null,
          cuit: cuit ?? null,
          credit_limit: existing.creditLimit,
          balance: existing.balance,
          blocked: existing.blocked,
          updated_at: now,
          email: existing.email ?? null,
          address: existing.address ?? null,
          tax_category: existing.taxCategory ?? null,
          notes: existing.notes ?? null,
          payment_terms_days: existing.paymentTermsDays ?? null,
          price_list_id: existing.priceListId ?? null,
        });
        return;
};
